import { defineStore } from 'pinia';
import type { MediaAsset } from '#constants/types/mediaBank';

type MediaBankMode = 'b2b' | 'b2c';

function getStorageKey(mode: MediaBankMode): string {
  return `mediaBank_favorites_${mode}`;
}

function loadFromStorage(mode: MediaBankMode): MediaAsset[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const stored = sessionStorage.getItem(getStorageKey(mode));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveToStorage(favorites: MediaAsset[], mode: MediaBankMode) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(getStorageKey(mode), JSON.stringify(favorites));
  } catch {
    // Ignore storage errors
  }
}

export const useMediaBankStore = defineStore('mediaBank', {
  state: () => ({
    favorites: [] as MediaAsset[],
    termsAccepted: false as boolean,
    mode: 'b2b' as MediaBankMode,
  }),

  getters: {
    isFavorite: (state) => (id: string): boolean => state.favorites.some((f) => f.id === id),
    favoriteCount: (state): number => state.favorites.length,
  },

  actions: {
    initFromStorage(mode?: MediaBankMode) {
      if (mode) {
        this.mode = mode;
      }
      this.favorites = loadFromStorage(this.mode);
    },

    toggleFavorite(asset: MediaAsset) {
      const idx = this.favorites.findIndex((f) => f.id === asset.id);
      if (idx > -1) {
        this.favorites.splice(idx, 1);
      } else {
        this.favorites.push(asset);
      }
      saveToStorage(this.favorites, this.mode);
    },

    clearFavorites() {
      this.favorites = [];
      saveToStorage([], this.mode);
    },

    acceptTerms() {
      this.termsAccepted = true;
    },
  },
});
